/**
 * MockDatabase
 * 
 * Declaring all classes and types in this file to mitigate circular dependencies
 */
import { v4 as uuidv4 } from 'uuid';
import { PaginatedData } from '../../../types';


type TypeMap = {
  string: string;
  UUID: string;
  GUID: string;
  number: number;
  boolean: boolean;
  foreignKey: string;
  primaryKey: string;
  publicKey: string;
  datetime: string;
  blob: Blob;
};

const TYPE_MAPPED_STRING_FIELDS = ['string', 'UUID', 'GUID', 'foreignKey', 'primaryKey', 'publicKey', 'datetime'];
const IDENTIFYING_TYPES = ['primaryKey', 'string', 'UUID', 'GUID'];

type FieldsToTableType<T extends Record<string, TableField | TableFieldForeignKey>> = {
  [K in T[string] as K['fieldName']]: TypeMap[K['type']] | null;
};

const TABLE_QUERY_OPERATOR_REDUCERS: string[] = ['min', 'max'] as const;
type TableQueryOperatorReducers = typeof TABLE_QUERY_OPERATOR_REDUCERS[number];
export type TableQueryOperator = '=' | 'startswith' & TableQueryOperatorReducers;
type FieldSimulator = (record: MockRecord) => any;
type TableQueryConditionEquals = {
    fieldName: string;
    operator?: '=';
    value: any;
    simulator?: FieldSimulator;
}
type TableQueryConditionMin = {
    fieldName: string;
    operator: 'min';
    value?: never;
    simulator?: FieldSimulator;
}
type TableQueryConditionMax = {
    fieldName: string;
    operator: 'max';
    value?: never;
    simulator?: FieldSimulator;
}
type TableQueryConditionStartsWith = {
    fieldName: string;
    operator: 'startswith';
    value: string;
    simulator?: FieldSimulator;
}
type TableQueryConditionReducer = TableQueryConditionMin | TableQueryConditionMax;
export type TableQueryCondition = TableQueryConditionEquals | TableQueryConditionStartsWith | TableQueryConditionReducer;
export type TableQueryConditionFieldDefinition = TableQueryCondition;

export type TableQuery = TableQueryCondition[];

type ForeignKeyConfig = {
    fieldName: string;
    foreignTableName: string;
    foreignTableFieldName: string;
    value: TypeMap[keyof TypeMap] | null;
    nullable: boolean;
}

type TableDefaultMethodFunc = {
    method: 'FUNC';
    onUpdate?: boolean;
    value?: never;
    func: (db: MockDatabase, table: MockTable, values: Record<string, any>) => any; // returns field value
}

type TableDefaultMethodUUID = {
    method: 'UUID';
    onUpdate?: never;
    value?: never;
    func?: never;
}

type TableDefaultMethodNow = {
    method: 'NOW';
    onUpdate?: boolean;
    value?: never;
    func?: never;
}

type TableDefaultValue = {
    method?: never;
    onUpdate?: never;
    value: TypeMap[keyof TypeMap] | null;
    func?: never;
}

export type TableFieldDefault = TableDefaultMethodFunc | TableDefaultMethodUUID | TableDefaultMethodNow | TableDefaultValue;

export type TableField = {
    type: keyof TypeMap;
    fieldName: string;
    nullable: boolean;
    index?: boolean;
    protected?: boolean;
    default?: TableFieldDefault;
};

export type TableFieldForeignKey = {
    foreignTableName: string
    foreignTableFieldName: string;
} & TableField;


class MockDBError extends Error {
    constructor(message: string) {
        super(message);
        console.error(`MockDatabase Error: ${message}`);
    }
}

export class MockDatabase {
    readonly tables: Record<string, MockTable> = {};

    addTable = (table: MockTable) => {
        this.tables[table.name] = table;
    }

    getTableByName = (tableName: string) => {
        return this.tables[tableName];
    }

    public readonly resolveGuidToId = (tableName: string, guid: string) => {
        const table = this.getTableByName(tableName);
        if (!table) {
            return null;
        }
        return table.resolveGuidToId(guid);
    }

    public readonly queryParamsToTableQuery = (params: URLSearchParams | Record<string, any>) => {
        const query: TableQuery = [];
        const filter = params instanceof URLSearchParams ? Object.fromEntries(params) : params;
        for (let [fieldName, value] of Object.entries(filter)) {
            query.push({
                fieldName,
                operator: '=',
                value
            });
        }
        return query;
    }

    public readonly sortRecords = (records: MockRecord[], sort?: string[]) => {
        if (!sort) {
            return records;
        }
        return records.sort((a, b) => {
            for (let sortEntry of sort) {
                let dir: 'asc' | 'desc' = 'asc';
                let col: string = sortEntry;
                if (sortEntry[0] === '-') {
                    dir = 'desc';
                    col = sortEntry.substring(1);
                }
                let res = 0;
                if (!a.fields[col] || !b.fields[col]) {
                    return res;
                }
                if (TYPE_MAPPED_STRING_FIELDS.indexOf(a.fields[col].type) > -1) {
                    if (dir === 'asc') {
                        res = a.values[col].localeCompare(b.values[col]);
                    } else {
                        res = a.values[col].localeCompare(b.values[col]);
                    }
                } else {
                    if (dir === 'asc') {
                        res = a.values[col] - b.values[col];
                    } else {
                        res = b.values[col] - a.values[col];
                    }
                }
                if (res) {
                    return res
                }
            }
            return 0;
        });
    }

    public readonly getPage = (records: MockRecord[], skip?: string, limit?: string) => {
        let nSkip = 0;
        let nLimit = 100;
        if (skip !== undefined) {
            try {
                nSkip = parseInt(skip);
            } catch {
                return records
            }
        }
        if (limit !== undefined) {
            try {
                nLimit = parseInt(limit);
            } catch {
                return records
            }
        }
        return records.splice(nSkip, nSkip+nLimit);
    }

    public readonly buildPaginatedResponse = (records: MockRecord[], skip?: string, limit?: string) => ({
        total: records.length,
        data: this.getPage(records, skip, limit)
    } as PaginatedData);
}


export class MockTable {
    constructor(db: MockDatabase) {
        this._db = db;
    }

    protected _db: MockDatabase;
    stopPropagation: boolean = false;
    name: string = '';
    private _dependencies: string[] = [];
    private _dependents: string[] = [];
    private _primaryKey: TableField | null = null;
    private _publicKey: TableField | null = null;
    // All indices are unordered. Only optimizes on = operator
    // Field name, value, primary key
    private _indices: Record<string, Record<string, string[]>> = {};
    // Primary key, record
    _records: Record<string, MockRecord> = {};
    fields: Record<string, TableField | TableFieldForeignKey> = {};
    simulatedFields: Record<string, (record: MockRecord) => any> = {};

    public readonly resolveGuidToId = (guid: string) => {
        const publicKeyName = this.getPublicKeyName();
        if (!publicKeyName) {
            return null;
        }
        const primaryKeyName = this.getPrimaryKeyName();
        if (!primaryKeyName) {
            return null;
        }
        const recs = this.query([
            {
                fieldName: publicKeyName,
                value: guid
            }
        ]);
        if (recs.length !== 1) {
            return null;
        }
        return recs[0].values[primaryKeyName];
    }

    protected readonly getIndex = (fieldName: string) => this._indices[fieldName];

    public readonly populateData = (data: Record<string, any>[]) => {
        data.forEach(entry => {
            this.createRecord(entry);
        });
    }

    public readonly addDependent = (tableName: string) => {
        const depIdx = this._dependents.indexOf(tableName);
        if (depIdx === -1) {
            this._dependents.push(tableName);
        }
    }
    public readonly removeDependent = (tableName: string) => {
        const idx = this._dependents.indexOf(tableName);
        if (idx < 0) {
            return
        }
        this._dependents.splice(idx, 1);
    }

    public readonly addDependency = (tableName: string) => {
        const depIdx = this._dependencies.indexOf(tableName);
        if (depIdx === -1) {
            this._dependencies.push(tableName);
        }
    }

    public readonly createRecord = (values: Record<string, any>, dependencies: MockRecord[] = []) => {
        values = this.beforeCreateInternal(this._db, this, values);
        const record = new MockRecord(this._db, this, values, dependencies);
        const indices = Object.values(this.fields).filter(field => field.index);
        indices.forEach(index => {
            if (!this._indices[index.fieldName]) {
                this._indices[index.fieldName] = {};
            }
            if (!this._indices[index.fieldName][record.values[index.fieldName]]) {
                this._indices[index.fieldName][record.values[index.fieldName]] = [];
            }
            this._indices[index.fieldName][record.values[index.fieldName]].push(record.values[this._primaryKey!.fieldName]);
        });
        this._records[record.values[this._primaryKey!.fieldName]] = record;
        this.afterCreateInternal(this._db, this, [record]);
        return record;
    }

    public readonly deleteRecord = (record: MockRecord, cascade?: boolean) => {
        this.beforeDeleteInternal(this._db, this, [record]);
        const primaryKeyVal = record.values[this._primaryKey!.fieldName];
        const indices = Object.values(this.fields).filter(field => field.index && field.type !== 'primaryKey');
        indices.forEach(index => {
            const indexPos = this._indices[index.fieldName][record.values[index.fieldName]].indexOf(primaryKeyVal);
            if (indexPos > -1) {
                this._indices[index.fieldName][record.values[index.fieldName]].splice(indexPos, 1);
            }
        });
        delete this._records[primaryKeyVal];
        record.destroy(this._db, this, cascade);
        this.afterDeleteInternal(this._db, this, [record]);
    }

    public readonly deleteRecords = (query: TableQuery, cascade?: boolean) => {
        const records = this.query(query);
        this.beforeDeleteInternal(this._db, this, records);
        records.forEach(record => {
            this.deleteRecord(record, cascade);
        });
        this.afterDeleteInternal(this._db, this, records);
    }

    public readonly updateRecords = (query: TableQuery | MockRecord[], data: Partial<FieldsToTableType<typeof this.fields>>) => {
        let records: MockRecord[] = [];
        if (query.length && query[0] instanceof MockRecord) {
            records = query as MockRecord[];
        } else {
            records = this.query(query as TableQuery);
        }
        data = this.beforeUpdateInternal(this._db, this, records, data);
        records.forEach(record => {
            record.update(this._db, this, data);
        });
        this.afterUpdateInternal(this._db, this, records);
    }

    public readonly one = (query: TableQuery) => {
        const recs = this.query(query);
        if (recs.length !== 1) {
            throw new MockDBError(`Invalid number of rows (received ${recs.length} and expected 1) returned from table ${this.name}. Query: ${JSON.stringify(query, null, 2)}`);
        }
        return recs[0];
    }

    public readonly first = (query: TableQuery, skipError?: boolean) => {
        const recs = this.query(query);
        if (recs.length === 0 && !skipError) {
            throw new MockDBError(`Invalid number of rows (received 0 and expected at least one) returned from table ${this.name}. Query: ${JSON.stringify(query, null, 2)}`);
        }
        return recs.length > 0 ? recs[0] : null;
    }

    private _queryOperatorsReducer = (records: MockRecord[], condition: TableQueryConditionReducer, recsSet: boolean) => {
        const {fieldName, operator, simulator} = condition;
        const isIndexed = this.fields[fieldName] && this.fields[fieldName].index;
        if (isIndexed) {
            const entries = this._indices[fieldName] ? Object.entries(this._indices[fieldName]) : [];
            if (!entries.length) {
                return [];
            }
            const winners = entries.reduce((acc, cur) => {
                if (!acc) {
                    return cur;
                }
                const accVal = acc[0];
                const curVal = cur[0];
                if (operator === 'max') {
                    return accVal > curVal ? acc : cur;
                }
                return accVal < curVal ? acc : cur;
            }, null as [string, string[]] | null);
            const intersetSet = new Set(winners ? winners[1] : []);
            if (!recsSet) {
                return Object.values(this._records).filter(record => intersetSet.has(record.values[this._primaryKey!.fieldName]));
            }
            return records.filter(record => intersetSet.has(record.values[this._primaryKey!.fieldName]));
        } else {
            if (!recsSet) {
                records = Object.values(this._records);
            }
            return [records.reduce((acc, cur) => {
                if (!acc) {
                    return cur;
                }
                let accVal = simulator ? simulator(acc) : acc.values[fieldName];
                let curVal = simulator ? simulator(cur) : cur.values[fieldName];
                if (operator === 'max') {
                    return accVal > curVal ? acc : cur;
                }
                return accVal < curVal ? acc : cur;
            }, null as MockRecord | null)].filter(record => record !== null)
        }
    }

    private _queryOperatorStartsWith = (records: MockRecord[], condition: TableQueryConditionEquals, recsSet: boolean) => {
        const {fieldName, value, simulator} = condition;
        if (!recsSet) {
            records = Object.values(this._records);
        }
        return records.filter(record => {
            const recVal = simulator ? simulator(record) : record.values[fieldName];
            return String(recVal).toLowerCase().startsWith(String(value).toLowerCase());
        });
    }

    private _queryOperatorEquals = (records: MockRecord[], condition: TableQueryConditionEquals, recsSet: boolean) => {
        const {fieldName, value, simulator} = condition;
        const isIndexed = this.fields[fieldName] && this.fields[fieldName].index;
        if (isIndexed) {
            const intersectArr = this._indices[fieldName] && this._indices[fieldName][value] ? this._indices[fieldName][value] : [];
            if (!intersectArr.length) {
                return [];
            }
            const intersetSet = new Set(intersectArr);
            if (!recsSet) {
                return Object.values(this._records).filter(record => intersetSet.has(record.values[this._primaryKey!.fieldName]));
            }
            return records.filter(record => intersetSet.has(record.values[this._primaryKey!.fieldName]));
        } else {
            if (!recsSet) {
                records = Object.values(this._records);
            }
            return records.filter(record => {
                const recVal = simulator ? simulator(record) : record.values[fieldName];
                return recVal === value;
            });
        }
    }

    public readonly all = () => Object.values(this._records)

    public readonly query = (query: TableQuery) => {
        if (!query.length) {
            return Object.values(this._records);
        }
        query = this.beforeQuery(this._db, this, query);
        const formattedQuery = this.formatQuery(query);
        let recsSet: boolean = false;
        let curRecs: MockRecord[] = [];
        for (let i = 0; i < formattedQuery.length; i++) {
            if (recsSet && !curRecs.length) {
                return [];
            }
            const {fieldName, operator} = formattedQuery[i];
            if (!this.fields[fieldName] && !this.simulatedFields[fieldName]) {
                throw new MockDBError(`Invalid field "${fieldName}" provided while querying table "${this.name}"`);
            }
            if (this.simulatedFields[fieldName]) {
                formattedQuery[i].simulator = this.simulatedFields[fieldName];
            }
            if (!operator || operator === '=') {
                curRecs = this._queryOperatorEquals(curRecs, formattedQuery[i] as TableQueryConditionEquals, recsSet);
                recsSet = true;
                continue
            }
            if (operator === 'startswith') {
                curRecs = this._queryOperatorStartsWith(curRecs, formattedQuery[i] as TableQueryConditionEquals, recsSet);
                recsSet = true;
                continue
            }
            curRecs = this._queryOperatorsReducer(curRecs, formattedQuery[i] as TableQueryConditionReducer, recsSet);
            recsSet = true;
        }
        return curRecs;
    }

    public readonly getPrimaryKeyName = () => {
        return this._primaryKey?.fieldName;
    }

    public readonly PrimaryKey = (config: Omit<TableField, 'type' | 'nullable' | 'index' | 'default'>) => {
        if (this._primaryKey) {
            throw new MockDBError(`Invalid configuration for table ${this.name}. Attempt to add more than one primary key`);
        }
        const nConfig: TableField = {
            ...config,
            type: 'primaryKey',
            nullable: false
        };
        this._primaryKey = nConfig;
        return nConfig;
    }

    public readonly getPublicKeyName = () => {
        return this._publicKey?.fieldName;
    }

    public readonly PublicKey = (config: Omit<TableField, 'type' | 'nullable' | 'index' | 'default'>) => {
        if (this._publicKey) {
            throw new MockDBError(`Invalid configuration for table ${this.name}. Attempt to add more than one public key`);
        }
        const nConfig: TableField = {
            ...config,
            type: 'publicKey',
            nullable: false,
            index: true,
        };
        this._publicKey = nConfig;
        return nConfig;
    }

    public readonly ForeignKey = (config: Omit<TableFieldForeignKey, 'type' | 'default'>) => {
        const nConfig: TableFieldForeignKey = {
            ...config,
            type: 'foreignKey'
        }
        const foreignTable = this._db.getTableByName(nConfig.foreignTableName);
        if (!foreignTable && config.nullable) {
            this.addDependency(nConfig.foreignTableName);
            return nConfig;
        }
        if (!foreignTable) {
            throw new MockDBError('Attempt to bind a foreign key to an unregistered table. Please be sure to register your tables in dependency order.');
        }
        const foreignField = foreignTable.fields[nConfig.foreignTableFieldName];
        if (!foreignField) {
            throw new MockDBError(`Attempt to bind a foreign key to an unrecognized field on table "${nConfig.foreignTableName}". Attempted field "${nConfig.foreignTableFieldName}"`);
        }
        if (IDENTIFYING_TYPES.indexOf(foreignField.type) === -1) {
            throw new MockDBError(`Attempt to bind a foreign key to non-identifying field on table "${nConfig.foreignTableName}". The field "${nConfig.foreignTableFieldName}" on table "${nConfig.foreignTableName}" is type "${foreignTable.fields[nConfig.foreignTableFieldName].type}"`);
        }
        foreignTable.addDependent(this.name);
        this.addDependency(nConfig.foreignTableName);
        return nConfig;
    }

    public readonly Field = (config: TableField) => {
        if (config.index && TYPE_MAPPED_STRING_FIELDS.indexOf(config.type) === -1) {
            throw new MockDBError(`Attempt to index non-string type mapped field. Field ${config.fieldName}; Type: ${config.type}`);
        }
        return config;
    }

    public readonly dump = (cascade?: boolean) => {
        if (!cascade && this._dependents.length) {
            throw new MockDBError(`Unable to dump table "${this.name}". The following tables are dependent: ${this._dependents.join(', ')}`);
        }
        this._dependents.forEach(dependent => {
            const foreignTable = this._db.getTableByName(dependent);
            foreignTable.dump(cascade)
        });
        this._dependencies.forEach(dependency => {
            const foreignTable = this._db.getTableByName(dependency);
            foreignTable.removeDependent(this.name);
        })
    }

    serialize: (record: MockRecord, db: MockDatabase) => any = (record: MockRecord, db: MockDatabase) => {
        // To be defined in table implementations
        return record.values;
    }

    serializeList: (records: MockRecord[], db: MockDatabase) => any[] = (records: MockRecord[], db: MockDatabase) => {
        // To be defined in table implementations if the list serialization differs from the inidivudal serialization
        return records.map(record => this.serialize(record, db));
    }

    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        // To be defined in subclass
        return query;
    }

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db: MockDatabase, table: MockTable, values: Record<string, any>) => {
        // To be defined in sublclass definition
        return values;
    }

    public readonly beforeCreateInternal: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db: MockDatabase, table: MockTable, values: Record<string, any>) => {
        // Do not overload
        this.stopPropagation = true;
        values = this.beforeCreate(db, table, values);
        return values;
    }

    protected afterCreate: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // To be defined in table definition
        return;
    }

    public readonly afterCreateInternal: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // Do not overload
        this.afterCreate(db, table, records);
        this.stopPropagation = false;
        return;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => {
        // To be defined in table definition
        return data;
    }

    public readonly beforeUpdateInternal: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => {
        // Do not overload
        this.stopPropagation = true;
        return this.beforeUpdate(db, table, records, data);
    }

    protected afterUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // To be defined in table definition
        return;
    }

    public readonly afterUpdateInternal: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // Do not overload
        this.afterUpdate(db, table, records);
        this.stopPropagation = false;
        return;
    }

    protected beforeDelete: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // To be defined in table definition
        return;
    }

    public readonly beforeDeleteInternal: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // Do not overload
        this.stopPropagation = true;
        this.beforeDelete(db, table, records);
        return;
    }

    protected afterDelete: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // To be defined in table definition
        return;
    }

    public readonly afterDeleteInternal: (db: MockDatabase, table: MockTable, records: MockRecord[]) => void = (db: MockDatabase, table: MockTable, records: MockRecord[]) => {
        // To be defined in table definition
        this.afterDelete(db, table, records);
        this.stopPropagation = false;
        return;
    }

    protected formatQuery: (query: TableQuery) => TableQuery = (query: TableQuery) => query;
}


export class MockRecord {
    constructor(db: MockDatabase, table: MockTable, values: Record<string, any>, dependencies: MockRecord[] = []) {
        this.tableName = table.name;
        this.fields = table.fields;
        const remainingValues = {...values};
        const remainingFields = {...table.fields};
        // Field name, Foreign table name, Foreign table field, value
        const foreignKeys: ForeignKeyConfig[] = [];
        const newRec: Record<string, any> = {};
        const entries = Object.entries(values);
        // Handle values provided
        for(let i = 0; i < entries.length; i++){
            const [fieldName, value] = entries[i];
            if (!table.fields[fieldName]) {
                throw new MockDBError(`Unrecognized field "${fieldName}" requested when creating record for "${table.name}"`);
            }
            // Will throw error if invalid
            this._validateFieldTypeAgainstValue(table, fieldName, value);
            if (value !== null && value !== undefined && table.fields[fieldName].protected) {
                throw new MockDBError(`Error encountered while creating record for ${table.name}. A value cannot be provided for protected field "${fieldName}"`);
            }
            if (table.fields[fieldName].protected && !table.fields[fieldName].default) {
                throw new MockDBError(`Invalid configuration for field "${fieldName}" on table "${table.name}". Protected fields must have defaults`);
            }
            const tableField = table.fields[fieldName] as TableFieldForeignKey;
            if (table.fields[fieldName].type === 'foreignKey') {
                foreignKeys.push({
                    fieldName,
                    foreignTableName: tableField.foreignTableName,
                    foreignTableFieldName: tableField.foreignTableFieldName,
                    value: value === undefined ? null : value,
                    nullable: tableField.nullable
                });
            } else {
                newRec[fieldName] = value;
            }
            delete remainingFields[fieldName];
            delete remainingValues[fieldName];
        }
        // Check for unrecognized values provided
        if (Object.keys(remainingValues).length) {
            throw new MockDBError(`Unrecognized fields requested when creating record for "${table.name}": ${JSON.stringify(remainingValues, null, 2)}`);
        }
        // Handle field defaults not provided in values
        const remFieldsEntries = Object.entries(remainingFields);
        for (let j = 0; j < remFieldsEntries.length; j++) {
            const [fieldName, config] = remFieldsEntries[j];
            if (config.type === 'foreignKey') {
                const tableField = table.fields[fieldName] as TableFieldForeignKey;
                foreignKeys.push({
                    fieldName,
                    foreignTableName: tableField.foreignTableName,
                    foreignTableFieldName: tableField.foreignTableFieldName,
                    value: null,
                    nullable: tableField.nullable
                });
                delete remainingFields[fieldName];
                continue;
            }
            if (config.type === 'primaryKey' || config.type === 'publicKey' || ((config.type === 'GUID' || config.type === 'UUID') && config.default?.method === 'UUID')) {
                newRec[fieldName] = uuidv4();
                delete remainingFields[fieldName];
                continue
            }
            if (config.nullable === true) {
                newRec[fieldName] = null;
                delete remainingFields[fieldName];
                continue
            }
            if (config.default && Object.hasOwn(config.default, 'value')) {
                newRec[fieldName] = config.default.value;
                delete remainingFields[fieldName];
                continue
            }
            if (config.default?.method === 'NOW') {
                newRec[fieldName] = this._getNowValue();
                delete remainingFields[fieldName];
                continue
            }
            if (config.default?.func) {
                newRec[fieldName] = config.default.func(db, table, {
                    ...values,
                    ...newRec
                });
            }
        }
        // Handle required fields not provided
        if (Object.keys(remainingFields).length) {
            throw new MockDBError(`Not enough values provided to create record for table "${table.name}". Missing: ${JSON.stringify(remainingFields, null, 2)}`);
        }
        // Process foreign keys
        foreignKeys.forEach(foreignKey => {
            const {fieldName, foreignTableName, foreignTableFieldName, value, nullable} = foreignKey;
            if (value === null && nullable) {
                // Nothing to be done. Relation can be set later
                return;
            }
            const foreignTable = db.getTableByName(foreignTableName);
            if (!foreignTable) {
                throw new MockDBError(`Unrecognized table "${foreignTableName}" referenced by foreign key field when creating a record for "${this.tableName}"`);
            }
            if (value === null) {
                // Auto process foreign key lookup
                const foreignRecord = dependencies.find(dep => dep.tableName === foreignTableName);
                if (!foreignRecord) {
                    throw new MockDBError(`Missing dependency when creating field for table "${table.name}". Missing dependency "${foreignTableName}"`);
                }
                const foreignRecordValues = foreignRecord.values as FieldsToTableType<typeof foreignTable.fields>;
                const foreignValue = foreignRecordValues[foreignTableFieldName]
                newRec[fieldName] = foreignValue;
                this.addRelated(foreignRecord);
            } else {
                const foreignRecord = foreignTable.query([{
                    fieldName: foreignTableFieldName,
                    value
                }]);
                if (foreignRecord.length === 0) {
                    throw new MockDBError(`Record not found on table ${foreignTableName} by ${foreignTableFieldName}: "${value}"`);
                }
                if (foreignRecord.length > 1) {
                    throw new MockDBError(`Foreign key binding for new record on ${this.tableName} resolved to too many records on ${foreignTableName}`);
                }
                newRec[fieldName] = value;
                this.addRelated(foreignRecord[0]);
            }
        });
        Object.entries(newRec).forEach(entry => {
            const [fieldName, value] = entry;
            // Will throw error if a type is not matched
            try {
                this._validateFieldTypeAgainstValue(table, fieldName, value);
            } catch(e) {
                throw new MockDBError(`Error encountered while creating record for table ${table.name}: ${e}`);
            }
        });
        const new_record: FieldsToTableType<typeof table.fields> = newRec;
        this.values = new_record;
    }

    readonly tableName: string;
    readonly values: any;
    readonly fields: Record<string, TableField | TableFieldForeignKey> = {};
    // Foreign table name, records
    // For example, this would contain a Bundle's parent
    private _related: Record<string, MockRecord[]> = {};
    // Foreign table name, records
    // For example, this would contain a Bundle's children
    private _dependents: Record<string, MockRecord[]> = {};

    private _getNowValue = () => new Date().toISOString();

    private _validateFieldTypeAgainstValue = (table: MockTable, fieldName: string, value: any) => {
        if ((value === null || value === undefined) && table.fields[fieldName].nullable) {
            return true;
        }
        if (table.fields[fieldName].type === 'blob' && (value instanceof Blob || value instanceof File)) {
            return true;
        }
        if (typeof value !== 'string' && TYPE_MAPPED_STRING_FIELDS.indexOf(table.fields[fieldName].type) > -1) {
            throw new MockDBError(`Invalid value type for field "${fieldName}" on table "${table.name}"; Value: ${value}; Type: ${typeof value}`);
        }
        if (typeof value !== table.fields[fieldName].type && TYPE_MAPPED_STRING_FIELDS.indexOf(table.fields[fieldName].type) === -1) {
            throw new MockDBError(`Invalid value type for field "${fieldName}" on table "${table.name}"; Value: ${value}; Type: ${typeof value}`);
        }
        return true;
    }

    update = (db: MockDatabase, table: MockTable, data: Partial<FieldsToTableType<typeof this.fields>>) => {
        let isTopLevel: boolean = false;
        if (!table.stopPropagation) {
            isTopLevel = true;
            data = table.beforeUpdateInternal(db, table, [this], data);
        }
        const remainingFields = {...table.fields};
        Object.entries(data).forEach(entry => {
            const [fieldName, value] = entry;
            if (!remainingFields[fieldName]) {
                throw new MockDBError(`Error encountered while updating record on table ${table.name}. Field "${fieldName}" not recognized`);
            }
            if (value === this.values[fieldName]) {
                // no-op
                delete remainingFields[fieldName];
                return
            }
            if (remainingFields[fieldName].protected) {
                throw new MockDBError(`Error encountered while updating record on table ${table.name}. Field "${fieldName}" is protected.`);
            }
            const fieldConfig = remainingFields[fieldName];
            if (fieldConfig.type === 'primaryKey') {
                throw new MockDBError(`Error encountered updating record on table ${table.name}. Cannot update primary keys at this time`);
            }
            if (fieldConfig.type === 'publicKey') {
                throw new MockDBError(`Error encountered updating record on table ${table.name}. Cannot update public keys at this time`);
            }
            if (fieldConfig.type === 'foreignKey') {
                const foreignTableName = (fieldConfig as TableFieldForeignKey).foreignTableName;
                const foreignTable = db.getTableByName(foreignTableName);
                if (!foreignTable) {
                    throw new MockDBError(`Failed to update foreign key for record on table "${this.tableName}". Could not find related table "${foreignTableName}"`);
                }
                const foreignTableFieldName = (fieldConfig as TableFieldForeignKey).foreignTableFieldName;

                if (this.values[fieldName]) {
                    const currentForeignRecord = foreignTable.query([
                        {fieldName: foreignTableFieldName, value: this.values[fieldName]}
                    ]);
                    for (let i = 0; i < currentForeignRecord.length; i++) {
                        this._removeRelated(currentForeignRecord[i]);
                    }
                }

                if (value) {
                    const foreignRecord = foreignTable.query([
                        {fieldName: foreignTableFieldName, value}
                    ]);
                    if (!foreignRecord.length || foreignRecord.length > 1) {
                        throw new MockDBError(
                            !foreignRecord.length
                            ? `Failed to update foreign key for record on table "${this.tableName}". Could not find a matching record for: ${foreignTableFieldName}=${value}`
                            : `Failed to update foreign key for record on table "${this.tableName}". The query "${foreignTableFieldName}=${value}" returned too many records.`
                        );
                    }
                    this.addRelated(foreignRecord[0]);
                }

                this.values[fieldName] = value;
            }
            // Will throw error if invalid
            this._validateFieldTypeAgainstValue(table, fieldName, value);
            this.values[fieldName] = value;
            delete remainingFields[fieldName];
        });
        // Handle defaults on update
        Object.values(remainingFields).forEach(config => {
            if (config.default?.onUpdate && config.default.method === 'NOW') {
                this.values[config.fieldName] = this._getNowValue();
            }
            if (config.default?.onUpdate && config.default.method === 'FUNC') {
                this.values[config.fieldName] = config.default.func(db, table, this);
            }
        });
        if (isTopLevel) {
            table.afterUpdateInternal(db, table, [this]);
        }
    }

    getRelated = (foreignTableName: string) => {
        return this._related[foreignTableName] || [];
    }

    getDependents = (foreignTableName: string) => {
        return this._dependents[foreignTableName] || [];
    }

    addRelated = (foreignRecord: MockRecord) => {
        const foreignTableName = foreignRecord.tableName;
        if (!this._related[foreignTableName]) {
            this._related[foreignTableName] = [];
        }
        this._related[foreignTableName].push(foreignRecord);
        foreignRecord.addDependent(this);
    }

    private _removeRelated = (foreignRecord: MockRecord) => {
        const foreignTableName = foreignRecord.tableName;
        if (!this._related[foreignTableName]) {
            return
        }
        const relatedIdx = this._related[foreignTableName].indexOf(foreignRecord);
        if (relatedIdx === -1) {
            return
        }
        this._related[foreignTableName].splice(relatedIdx, 1);
        foreignRecord.removeDependent(this);
    }

    addDependent = (record: MockRecord) => {
        const foreignTableName = record.tableName;
        if (!this._dependents[foreignTableName]) {
            (this._dependents[foreignTableName] as MockRecord[]) = [] as MockRecord[];
        }
        this._dependents[foreignTableName].push(record);
    }

    removeDependent = (record: MockRecord) => {
        const foreignTableName = record.tableName;
        if (!this._dependents[foreignTableName]) {
            return;
        }
        const depIdx = this._dependents[foreignTableName].indexOf(record);
        if (depIdx > -1) {
            this._dependents[foreignTableName].splice(depIdx, 1);
        }
    }

    destroy = (db: MockDatabase, table: MockTable, cascade?: boolean) => {
        if (!cascade && this._dependents.length) {
            throw new MockDBError(`Unable to drop record from table as the following tables are dependent: ${Object.keys(this._dependents).join(', ')}`);
        }
        let isTopLevel: boolean = false;
        if (!table.stopPropagation) {
            isTopLevel = true;
            table.beforeDeleteInternal(db, table, [this]);
        }
        if (this._dependents.length) {
            Object.values(this._dependents).flat().forEach(dependent => {
                const foreignTable = db.getTableByName(dependent.tableName);
                if (!foreignTable) {
                    throw new MockDBError(`Unexpected table ${dependent.tableName} encountered on destory record`);
                }
                foreignTable.deleteRecord(dependent, cascade);
            });
        }
        this._dependents = {};
        Object.values(this._related).flat().forEach(related => related.removeDependent(this));
        if (isTopLevel) {
            table.afterDeleteInternal(db, table, [this]);
        }
    }
}